import * as React from 'react';
import { Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { AppointmentType } from '@prisma/client';
import { Option } from '../../interfaces';
import SelectInput from './SelectInput';
import DollarInput from './DollarInput';
import {
  getArtistOptions,
  getArtistsForService,
} from '../../services/business';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import {
  CreateAptValues,
  DEFAULT_APT_LENGTH,
  DEFAULT_CONSULT_LENGTH,
  allowOverlappingOptions,
  waiverOptions,
} from '../../utils/form/appointmentForm';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import { getBusinessTripName } from '../../helpers/businesses';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import { isAppointmentWaiverUpdatable } from '../../helpers/waivers';
import {
  getDepositPlacementSelectInput,
  getLengthOptions,
  isAppointmentDepositPaid,
  isDepositPlacementRequired,
  isWaiverSelectable,
} from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import useConfirm from '../../hooks/useConfirm';

const priceOptions: Option[] = [
  { value: 'FIXED', label: 'Set Price' },
  { value: 'RANGE', label: 'Price Range' },
  { value: 'HOURLY', label: 'Hourly Price' },
];

export default function AppointmentDetails() {
  const confirm = useConfirm();
  const { user: loadedUser } = useUser();
  const { values, setFieldValue, touched, setFieldTouched } =
    useFormikContext<CreateAptValues>();
  const appointmentType = values.appointmentType as AppointmentType;

  const { selectedAppointment } = useCalendarSelectedAppointment();
  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);

  const { data: waiverData, isFetching: isFetchingWaivers } = useGetWaivers();
  const showWaiverSelect = isWaiverSelectable({
    waiverData,
    hasWaiversFeature,
    appointmentType,
  });

  const isWaiversDisabled =
    !!selectedAppointment &&
    (!isAppointmentWaiverUpdatable(selectedAppointment?.startDateTime) ||
      selectedAppointment?.waiverRequest?.status === 'SIGNED');

  const [artistOptions, setArtistOptions] = React.useState<Option[]>();

  const isConsultation = appointmentType === 'CONSULTATION';
  const isWalkInOrConsultation =
    appointmentType === 'WALK_IN' || appointmentType === 'CONSULTATION';

  const serviceOptions = React.useMemo(() => {
    const options: Option[] = [{ value: '0', label: 'Tattoo' }];
    if (!loadedUser?.business?.services) {
      return options;
    }
    loadedUser.business.services.forEach(service => {
      const existing = options.find(
        option => option.value === String(service.serviceId),
      );
      if (!existing) {
        options.push({
          value: String(service.serviceId),
          label: service.serviceName,
        });
      }
    });
    return options;
  }, [loadedUser?.business?.services]);

  const tripOptions = React.useMemo(() => {
    const options: Option[] = [{ value: '', label: 'No Trip (Home Studio)' }];
    if (!loadedUser?.business?.businessTrips) {
      return options;
    }
    loadedUser.business.businessTrips.forEach(trip => {
      const existing = options.find(option => option.value === trip.tripId);
      if (!existing) {
        options.push({
          value: trip.tripId,
          label: getBusinessTripName(trip),
        });
      }
    });
    return options;
  }, [loadedUser?.business?.businessTrips]);

  React.useEffect(() => {
    setFieldValue('requestWaiver', showWaiverSelect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWaiverSelect]);

  const service = loadedUser?.business.services?.find(
    s => String(s.serviceId) === String(values.serviceId),
  );

  const isTattoo = !service || service.serviceId === 0;

  const appointmentLengthOptions = getLengthOptions({
    user: loadedUser,
    appointmentType: values.appointmentType as AppointmentType,
    serviceId: values.serviceId ? Number(values.serviceId) : undefined,
  });

  const depositPaidOptions = [
    { value: false, label: 'No' },
    { value: true, label: 'Yes' },
  ];

  const { artistLabel, artistPlaceholder } = React.useMemo(() => {
    if (!isTattoo) {
      return {
        artistLabel: 'Provider *',
        artistPlaceholder: 'Select service provider',
      };
    }
    return { artistLabel: 'Artist *', artistPlaceholder: 'Select artist' };
  }, [isTattoo]);

  // Only run on initial load or if loadedUser changes
  React.useEffect(() => {
    if (
      loadedUser?.business.defaultTattooDeposit &&
      !isWalkInOrConsultation &&
      !selectedAppointment
    ) {
      setFieldValue('depositPrice', loadedUser.business.defaultTattooDeposit);
      setFieldValue('price', loadedUser.business.defaultTattooDeposit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedUser]);

  React.useEffect(() => {
    async function fetchArtistOptions() {
      if (loadedUser) {
        if (!values.serviceId || values.serviceId === '0') {
          const tempArtistOptions = await getArtistOptions(loadedUser);
          setArtistOptions(tempArtistOptions);
        } else if (service) {
          const tempArtistOptions = await getArtistsForService(
            loadedUser,
            service,
          );
          setArtistOptions(tempArtistOptions);
        }
      }
    }

    fetchArtistOptions();
  }, [loadedUser, values.serviceId, service]);

  const onChangeService = (name: string, value: string) => {
    setFieldValue(name, value);
    if (value !== values.serviceId) {
      const newService = loadedUser?.business.services?.find(
        s => s.serviceId === Number(value),
      );
      const isNewServiceTattoo = !newService || newService?.serviceId === 0;

      setFieldValue('startDateTime', '');
      setFieldValue('additionalAppointments', []);

      const newLengthOptions = getLengthOptions({
        user: loadedUser,
        appointmentType: values.appointmentType as AppointmentType,
        serviceId: newService ? newService.serviceId : undefined,
      });

      const isCurrentLengthInvalid = !newLengthOptions?.some(
        length => length.value === values.appointmentLengthInMinutes,
      );

      if (isCurrentLengthInvalid) {
        setFieldValue('appointmentLengthInMinutes', '');
      }

      if (
        !touched.appointmentLengthInMinutes &&
        !selectedAppointment &&
        appointmentType === 'CONSULTATION'
      ) {
        setFieldValue('appointmentLengthInMinutes', DEFAULT_CONSULT_LENGTH);
      }

      if (newService && !isNewServiceTattoo && !selectedAppointment) {
        if (!touched.appointmentLengthInMinutes && !isConsultation) {
          setFieldValue(
            'appointmentLengthInMinutes',
            String(newService.defaultDurationInMinutes),
          );
        }

        if (!touched.price && !isConsultation) {
          setFieldValue('price', newService.defaultPrice);
        }

        if (!touched.depositPrice && !isWalkInOrConsultation) {
          setFieldValue('depositPrice', newService.defaultDepositPrice || 0);
        }
      } else if (isNewServiceTattoo && !selectedAppointment) {
        if (!touched.price && !isConsultation) {
          setFieldValue('price', loadedUser?.business.defaultTattooDeposit);
        }
        if (!touched.depositPrice && !isWalkInOrConsultation) {
          setFieldValue(
            'depositPrice',
            loadedUser?.business.defaultTattooDeposit,
          );
        }
        if (!touched.appointmentLengthInMinutes) {
          setFieldValue(
            'appointmentLengthInMinutes',
            isConsultation ? DEFAULT_CONSULT_LENGTH : DEFAULT_APT_LENGTH,
          );
        }
      }
    }
  };

  const title = isConsultation
    ? 'Consultation Details'
    : 'Apppointment Details';
  const priceDisabled =
    !!selectedAppointment?.checkoutSessionActive ||
    !!selectedAppointment?.checkoutSessionCompleted;

  return (
    <div className="input-group appointment-details">
      <Typography variant="h2">{title}</Typography>
      <div className="inputs">
        <div className="input-row">
          <SelectInput
            values={values}
            setFieldValue={onChangeService}
            name="serviceId"
            label="Service"
            options={serviceOptions}
            placeholder="Select a service"
            disabled={false}
            hidden={
              !loadedUser?.business.services ||
              loadedUser?.business.services.length === 0
            }
          />
        </div>
        {artistOptions && (
          <div className="input-row">
            <SelectInput
              values={values}
              setFieldValue={setFieldValue}
              name="employeeId"
              label={artistLabel}
              options={artistOptions}
              placeholder={artistPlaceholder}
              disabled={false}
            />
          </div>
        )}
        {appointmentLengthOptions && (
          <div className="input-row">
            <SelectInput
              values={values}
              setFieldValue={async (name, value) => {
                const isIncreased =
                  parseInt(value || '0', 10) >
                  parseInt(values.appointmentLengthInMinutes || '0', 10);
                const requireReset =
                  isIncreased &&
                  (values.startDateTime ||
                    values.additionalAppointments?.length);
                if (requireReset) {
                  await confirm({
                    dismissible: true,
                    title: 'Date & Time Reset',
                    message: `Your selected date & time will be reset. Please re-select a new date & time for this ${
                      isConsultation ? 'consultation' : 'appointment'
                    }.`,
                    isSmall: true,
                    positiveText: 'Okay',
                    negativeText: '',
                  });
                  setFieldValue('additionalAppointments', []);
                  setFieldValue('startDateTime', '');
                }
                setFieldValue(name, value);
              }}
              name="appointmentLengthInMinutes"
              label="Length *"
              options={appointmentLengthOptions}
              placeholder="Select duration"
              disabled={false}
            />
          </div>
        )}
        {!isWalkInOrConsultation && (
          <>
            <div className="input-row">
              <DollarInput
                name="depositPrice"
                label="Deposit Amount *"
                placeholder="Enter Amount"
                values={values}
                setFieldValue={setFieldValue}
                disabled={
                  selectedAppointment?.status === 'CONFIRMED' ||
                  !!selectedAppointment?.checkoutSessionActive ||
                  !!selectedAppointment?.checkoutSessionCompleted
                }
                hidden={false}
                setFieldTouched={setFieldTouched}
              />
            </div>
            <div className="input-row">
              <SelectInput
                name="depositPaid"
                label="Deposit Already Paid"
                placeholder="Has the deposit been paid?"
                values={{
                  ...values,
                  depositPaid:
                    (values.depositPrice ?? 0) > 0 ? values.depositPaid : false,
                }}
                setFieldValue={setFieldValue}
                disabled={
                  selectedAppointment?.status === 'CONFIRMED' ||
                  !!selectedAppointment?.checkoutSessionActive ||
                  !!selectedAppointment?.checkoutSessionCompleted ||
                  (values.depositPrice ?? 0) <= 0 ||
                  (!!selectedAppointment &&
                    isAppointmentDepositPaid(selectedAppointment))
                }
                hidden={false}
                options={depositPaidOptions}
              />
            </div>
          </>
        )}
        {!isConsultation && (
          <>
            <div className="input-row">
              <SelectInput
                values={values}
                setFieldValue={setFieldValue}
                name="priceType"
                label="Price Type"
                options={priceOptions}
                placeholder="Select price type"
                disabled={priceDisabled}
              />
              <DollarInput
                name="price"
                label="Set Price"
                placeholder="Enter price"
                values={values}
                setFieldValue={setFieldValue}
                disabled={priceDisabled}
                hidden={values.priceType !== 'FIXED'}
                setFieldTouched={setFieldTouched}
              />
              <DollarInput
                name="hourlyPrice"
                label="Hourly Price"
                placeholder="Enter hourly price"
                values={values}
                setFieldValue={setFieldValue}
                disabled={priceDisabled}
                hidden={values.priceType !== 'HOURLY'}
                setFieldTouched={setFieldTouched}
              />
            </div>
            <div className="input-row">
              <DollarInput
                name="minPrice"
                label="Min Price"
                placeholder="Enter min"
                values={values}
                setFieldValue={setFieldValue}
                disabled={priceDisabled}
                hidden={values.priceType !== 'RANGE'}
                setFieldTouched={setFieldTouched}
              />
              <DollarInput
                name="maxPrice"
                label="Max Price"
                placeholder="Enter max"
                values={values}
                setFieldValue={setFieldValue}
                disabled={priceDisabled}
                hidden={values.priceType !== 'RANGE'}
                setFieldTouched={setFieldTouched}
              />
            </div>
          </>
        )}
        {showWaiverSelect && (
          <div className="input-row">
            <SelectInput
              name="requestWaiver"
              label="Waiver"
              placeholder="Select waiver option"
              options={waiverOptions}
              disabled={isFetchingWaivers || isWaiversDisabled}
              values={values}
              setFieldValue={setFieldValue}
              tooltip={`If you choose "Collect through Porter", we'll automatically send the client a link on the morning of their appointment to complete the waiver before they come in for their appointment.`}
            />
          </div>
        )}

        {isConsultation && (
          <div className="input-row">
            <SelectInput
              name="allowOverlapping"
              label="Double Book"
              placeholder="Select an option"
              options={allowOverlappingOptions}
              disabled={!isConsultation}
              values={values}
              setFieldValue={setFieldValue}
            />
          </div>
        )}

        {tripOptions && tripOptions.length > 1 && (
          <div className="input-row">
            <SelectInput
              name="businessTripId"
              label="Trip"
              placeholder="Select a trip"
              options={tripOptions}
              disabled={false}
              values={values}
              setFieldValue={setFieldValue}
            />
          </div>
        )}

        {isDepositPlacementRequired(
          values.additionalAppointments ?? [],
          values.depositPrice,
        ) && (
          <div className="input-row">
            <SelectInput
              values={values}
              setFieldValue={setFieldValue}
              {...getDepositPlacementSelectInput({
                required: true,
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
